import { type MessageDescriptor } from 'react-intl';

import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import {
	type UseResponseStyleSettingsParams,
	useResponseStyleSettings,
} from '@atlassian/conversation-assistant-store/controllers/response-style-settings';

import { THINKING_VERB_SETS } from './thinkingVerbSets';

/** Stable, well-distributed string hash (djb2). Used to pick a verb per spell. */
const hashString = (value: string): number => {
	let hash = 5381;
	for (let i = 0; i < value.length; i++) {
		hash = (hash * 33) ^ value.charCodeAt(i);
	}
	// Coerce to a non-negative 32-bit integer.
	return hash >>> 0;
};

export type UsePersonalityThinkingVerbParams = {
	/** Whether the default-Rovo thinking treatment can render for the active turn. */
	enabled: boolean;
	/**
	 * Identifies the current thinking spell — the id of the human turn being
	 * answered. Stable while Rovo thinks and streams, so the verb holds steady;
	 * a new turn yields a new id and a new verb. `undefined` when there is no
	 * active turn to key from.
	 */
	spellId: string | undefined;
	assistanceServiceParams: UseResponseStyleSettingsParams['assistanceServiceParams'];
};

export type UsePersonalityThinkingVerbResult = {
	/**
	 * The verb to render in place of the default "{name} is thinking", or
	 * `undefined` when personality thinking verbs don't apply (gate off, preset
	 * still resolving, or no set for the active preset). Callers fall back to the
	 * plain default when this is `undefined`.
	 */
	verbMessage: MessageDescriptor | undefined;
};

/**
 * Resolves the personality-flavored thinking-indicator verb for the active
 * preset. One verb is chosen per thinking spell (keyed off `spellId`) and stays
 * fixed for that spell; the next turn picks another. Selection is a pure
 * function of `(preset, spellId)` — no timers, no persisted rotation state — so
 * it is stable across re-renders and safe under React Strict Mode.
 *
 * Everything is gated behind the `rovo_chat_personality_presets` and
 * `rovo_chat_personality_thinking_verb` experiments: gate-off (or a still-loading
 * preset) returns `undefined`, so the indicator keeps the plain "Rovo is
 * thinking" baseline. The `['rovoUserSettings']` query is a shared cache warmed
 * at chat bootstrap, so the preset is normally already known and the verb
 * resolves on the first render.
 */
export const usePersonalityThinkingVerb = ({
	enabled,
	spellId,
	assistanceServiceParams,
}: UsePersonalityThinkingVerbParams): UsePersonalityThinkingVerbResult => {
	// Double-gated: the feature-level Personality Presets experiment AND the
	// surface-specific thinking-verb experiment. Read each once — repeat calls
	// double-count exposure. The surface experiment stays last in the `&&`
	// (no-preconditioning): its exposure fires only for the presets-eligible
	// population.
	const gateEnabled =
		enabled &&
		spellId !== undefined &&
		isExperimentEnabled('rovo_chat_personality_presets') &&
		isExperimentEnabled('rovo_chat_personality_thinking_verb');

	const { responseStyle, isLoading } = useResponseStyleSettings({
		assistanceServiceParams,
	});

	if (!gateEnabled || isLoading || spellId === undefined) {
		return { verbMessage: undefined };
	}

	const set = THINKING_VERB_SETS[responseStyle.responseTone] ?? [];
	if (set.length === 0) {
		return { verbMessage: undefined };
	}

	return { verbMessage: set[hashString(spellId) % set.length] };
};
